import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { css } from '@emotion/core'
import { useStatusOverlay } from '@fs/zion-ui'
import { Link } from '@fs/zion-router'
import { useTranslation } from 'react-i18next'

export default function DmcCopyToClipboardTooltip({
  value,
  body,
  tooltipText,
  overlayMessage,
  path,
  newTab,
  nextToText,
}) {
  const { t } = useTranslation()
  const [onEnterDelayHandler, setOnEnterDelayHandler] = useState()
  const [onLeaveDelayHandler, setOnLeaveDelayHandler] = useState()
  const showStatusOverlay = useStatusOverlay()
  const [showToolTip, setShowToolTip] = useState(false)
  const triggerRef = useRef(null)
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    return () => {
      clearTimeout(onEnterDelayHandler)
      clearTimeout(onLeaveDelayHandler)
    }
  }, [onEnterDelayHandler, onLeaveDelayHandler])
  const preserveWhiteSpace = css`
    white-space: pre-wrap;
  `
  const showTooltipCss = css`
    display: block;
    position: fixed;
    background-color: rgb(30, 30, 30);
    border: none;
    padding: 5px 7px;
    border-radius: 6px;
    font-size: 12px;
    opacity: 1;
    white-space: nowrap;
    z-index: 100;
    color: rgb(240, 240, 240);
    font-size: 14px;
    transition:
      font-size 250ms,
      background-color 250ms;
    :hover {
      font-size: 1rem;
      background-color: rgb(90, 90, 90);
    }
    :active {
      background-color: rgb(125, 125, 125);
      font-size: 0.75rem;
    }
  `

  const tooltipWithText = css`
    position: relative;
  `

  const copyOnClick = () => {
    navigator.clipboard.writeText(value)
    showStatusOverlay({
      message: overlayMessage || `${value.replace(/ /g, '\u00A0')} copied to clipboard`,
    })
  }

  const handleMouseEnterText = () => {
    if (onLeaveDelayHandler) {
      clearTimeout(onLeaveDelayHandler)
    }
    setOnEnterDelayHandler(
      setTimeout(() => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect()
          setTooltipPos({
            top: nextToText ? rect.top : rect.bottom + 4,
            left: nextToText ? rect.right + 8 : rect.left + rect.width / 2,
          })
        }
        setShowToolTip(true)
      }, 100)
    )
    clearTimeout(onEnterDelayHandler)
  }

  const handleMouseLeaveText = () => {
    if (onEnterDelayHandler) {
      clearTimeout(onEnterDelayHandler)
    }
    setOnLeaveDelayHandler(
      setTimeout(() => {
        setShowToolTip(false)
      }, 250)
    )
    clearTimeout(onLeaveDelayHandler)
  }

  return (
    <span
      css={tooltipWithText}
      onMouseEnter={handleMouseEnterText}
      onMouseLeave={handleMouseLeaveText}
      ref={triggerRef}
    >
      {showToolTip &&
        createPortal(
          <button
            data-nocopy
            css={[
              showTooltipCss,
              {
                top: tooltipPos.top,
                left: tooltipPos.left,
                transform: nextToText ? 'none' : 'translateX(-50%)',
              },
            ]}
            type="button"
            onClick={copyOnClick}
          >
            {tooltipText || t('tooltip.button.text', 'Copy')}
          </button>,
          document.body
        )}
      {!path ? (
        <span css={preserveWhiteSpace}>{body || value}</span>
      ) : (
        <span role="link" tabIndex={0}>
          {path && (
            <Link to={path} target={newTab ? '_blank' : '_self'}>
              {body || value}
            </Link>
          )}
        </span>
      )}
    </span>
  )
}
